import React from 'react';
import { ChevronLeft, MapPin, Clock, MessageCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/Button';
import { StatusTimeline } from '../components/StatusTimeline';
import { Badge } from '../components/Badge';
interface TripDetailProps {
  tripId: string;
  onBack: () => void;
  onStartRental: () => void;
  onEndRental: () => void;
  onChat: () => void;
}
export function TripDetail({
  onBack,
  onStartRental,
  onChat
}: TripDetailProps) {
  const mockTrip = {
    status: 'approved',
    itemTitle: 'Professional Power Drill Set',
    itemImage: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=400&q=80',
    startDate: 'Dec 20, 2024',
    endDate: 'Dec 23, 2024',
    pickupLocation: 'Starbucks on Market St',
    pickupAddress: '123 Market St, San Francisco, CA 94103',
    pickupWindow: '9:00 AM - 6:00 PM',
    owner: {
      name: 'Sarah M.',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80'
    },
    pricing: {
      rental: 45,
      platformFee: 5,
      deposit: 100,
      total: 50
    }
  };
  const timelineSteps = [{
    label: 'Booking requested',
    status: 'completed' as const,
    timestamp: 'Dec 10, 2:30 PM'
  }, {
    label: 'Approved by owner',
    status: 'completed' as const,
    timestamp: 'Dec 10, 4:15 PM'
  }, {
    label: 'Ready for pickup',
    status: 'active' as const,
    timestamp: 'Dec 20, 9:00 AM'
  }, {
    label: 'Rental started',
    status: 'pending' as const
  }, {
    label: 'Rental completed',
    status: 'pending' as const
  }];
  return <div className="min-h-screen bg-cream pb-24" data-id="element-1635">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-sand" data-id="element-1636">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between" data-id="element-1637">
          <button onClick={onBack} className="p-2 -ml-2" data-id="element-1638">
            <ChevronLeft size={24} className="text-charcoal" data-id="element-1639" />
          </button>
          <h1 className="font-semibold text-charcoal" data-id="element-1640">Trip Details</h1>
          <div className="w-10" data-id="element-1641" />
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-6" data-id="element-1642">
        {/* Item Card */}
        <div className="bg-white rounded-ios-lg overflow-hidden shadow-ios" data-id="element-1643">
          <img src={mockTrip.itemImage} alt={mockTrip.itemTitle} className="w-full h-48 object-cover" data-id="element-1644" />
          <div className="p-4" data-id="element-1645">
            <div className="flex items-start justify-between mb-2" data-id="element-1646">
              <h2 className="text-xl font-bold text-charcoal flex-1" data-id="element-1647">
                {mockTrip.itemTitle}
              </h2>
              <Badge variant="success" data-id="element-1648">Approved</Badge>
            </div>
            <p className="text-sm text-moss-gray" data-id="element-1649">
              {mockTrip.startDate} - {mockTrip.endDate}
            </p>
          </div>
        </div>

        {/* Pickup Info */}
        <div className="bg-white rounded-ios-lg p-4 shadow-ios space-y-4" data-id="element-1650">
          <h3 className="font-semibold text-charcoal" data-id="element-1651">Pickup Information</h3>

          <div className="flex gap-3" data-id="element-1652">
            <MapPin size={20} className="text-dark-green flex-shrink-0 mt-0.5" data-id="element-1653" />
            <div data-id="element-1654">
              <p className="font-medium text-charcoal" data-id="element-1655">
                {mockTrip.pickupLocation}
              </p>
              <p className="text-sm text-moss-gray mt-1" data-id="element-1656">
                {mockTrip.pickupAddress}
              </p>
            </div>
          </div>

          <div className="flex gap-3" data-id="element-1657">
            <Clock size={20} className="text-dark-green flex-shrink-0 mt-0.5" data-id="element-1658" />
            <div data-id="element-1659">
              <p className="font-medium text-charcoal" data-id="element-1660">Pickup Window</p>
              <p className="text-sm text-moss-gray mt-1" data-id="element-1661">
                {mockTrip.pickupWindow}
              </p>
            </div>
          </div>

          <div className="pt-3 border-t border-sand" data-id="element-1662">
            <Button variant="secondary" fullWidth onClick={onChat} data-id="element-1663">
              <MessageCircle size={20} data-id="element-1664" />
              Message Sarah M.
            </Button>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-ios-lg p-4 shadow-ios" data-id="element-1665">
          <h3 className="font-semibold text-charcoal mb-4" data-id="element-1666">Rental Progress</h3>
          <StatusTimeline steps={timelineSteps} data-id="element-1667" />
        </div>

        {/* Payment Summary */}
        <div className="bg-white rounded-ios-lg p-4 shadow-ios space-y-2" data-id="element-1668">
          <h3 className="font-semibold text-charcoal mb-3" data-id="element-1669">Payment Summary</h3>
          <div className="flex justify-between text-sm" data-id="element-1670">
            <span className="text-moss-gray" data-id="element-1671">Rental amount</span>
            <span className="font-medium text-charcoal" data-id="element-1672">
              ${mockTrip.pricing.rental}
            </span>
          </div>
          <div className="flex justify-between text-sm" data-id="element-1673">
            <span className="text-moss-gray" data-id="element-1674">Platform fee</span>
            <span className="font-medium text-charcoal" data-id="element-1675">
              ${mockTrip.pricing.platformFee}
            </span>
          </div>
          <div className="flex justify-between text-sm pt-2 border-t border-sand" data-id="element-1676">
            <span className="text-moss-gray" data-id="element-1677">Deposit (held)</span>
            <span className="font-medium text-charcoal" data-id="element-1678">
              ${mockTrip.pricing.deposit}
            </span>
          </div>
          <div className="flex justify-between pt-2 border-t border-sand" data-id="element-1679">
            <span className="font-semibold text-charcoal" data-id="element-1680">Total paid</span>
            <span className="font-bold text-dark-green" data-id="element-1681">
              ${mockTrip.pricing.total}
            </span>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-sky-blue/20 border border-sky-blue/30 rounded-ios-lg p-4 flex gap-3" data-id="element-1682">
          <AlertCircle size={20} className="text-dark-green flex-shrink-0 mt-0.5" data-id="element-1683" />
          <p className="text-sm text-charcoal" data-id="element-1684">
            Meet at the pickup location during the window above. You'll start
            the rental by taking photos of the item's condition.
          </p>
        </div>

        {/* CTA */}
        <Button variant="primary" fullWidth size="lg" onClick={onStartRental} data-id="element-1685">
          Start Rental
        </Button>
      </div>
    </div>;
}